/**
 * Client-side analytics for patch application
 *
 * Tracks patch application outcomes and reports them back to the server.
 * Uses navigator.sendBeacon for reliable delivery even on page unload.
 *
 * Batches reports to avoid excessive network requests.
 * Reports are sent:
 * - Every 30 seconds if there are pending events
 * - On page unload (via sendBeacon)
 */

import { reportPageUrl } from '@helpbob/shared/dist/page-url';

export interface PatchEvent {
  patchId: string;
  /**
   * Lifecycle events for an individual patch.
   *
   * Visitor-facing (steady-state browsing):
   *   - applied         — patch successfully applied on initial load
   *   - dynamic_applied — patch applied to a node added later via MutationWatcher
   *   - failed          — selector matched but mutation threw
   *   - rolled_back     — patch removed (e.g. by HelpBob.removeAll())
   *
   * Reviewer-facing (in-page review):
   *   - review_opened     — reviewer loaded the page with a valid signed link
   *                         (per-session in panel mode, per-patch in the
   *                         legacy single-patch bar)
   *   - review_approved   — reviewer accepted the patch
   *   - review_rejected   — reviewer rejected the patch (reason in audit log)
   *   - review_reinstated — reviewer pulled a rejected patch back to pending
   *   - review_disabled   — reviewer switched an active patch off
   *   - review_closed     — reviewer closed the review UI without deciding
   */
  event: 'applied' | 'failed' | 'rolled_back' | 'dynamic_applied'
       | 'review_opened' | 'review_approved' | 'review_rejected' | 'review_closed'
       | 'review_reinstated' | 'review_disabled';
  selector: string;
  timestamp: number;
}

export interface AnalyticsReport {
  url: string;
  events: PatchEvent[];
  sessionId: string;
  // Kein userAgent mehr (scanner#362): der Server speichert ihn nicht, also
  // wird er auch nicht verschickt.
}

export class PatchAnalytics {
  private events: PatchEvent[] = [];
  private serverUrl: string;
  private apiKey: string;
  private sessionId: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private boundOnUnload: (() => void) | null = null;
  private running = false;

  private static FLUSH_INTERVAL_MS = 30_000;

  /** Hoechstens so viele Ereignisse je Anfrage (Sicherheitspruefung
   *  13.10.2026): der Server nimmt an /report hoechstens 64 KB an; 100
   *  Ereignisse mit langen Selektoren bleiben deutlich darunter. */
  static REPORT_CHUNK = 100;

  constructor(serverUrl: string, apiKey: string) {
    this.serverUrl = serverUrl;
    this.apiKey = apiKey;
    this.sessionId = this.generateSessionId();
  }

  start(): void {
    if (this.running) return;
    this.running = true;

    // Periodic flush
    this.flushTimer = setInterval(() => this.flush(), PatchAnalytics.FLUSH_INTERVAL_MS);

    // Flush on page unload
    this.boundOnUnload = () => this.flushBeacon();
    window.addEventListener('visibilitychange', this.boundOnUnload);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;

    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.boundOnUnload) {
      window.removeEventListener('visibilitychange', this.boundOnUnload);
      this.boundOnUnload = null;
    }

    // Final flush
    this.flushBeacon();
  }

  /** Record a patch application event */
  record(patchId: string, event: PatchEvent['event'], selector: string): void {
    this.events.push({
      patchId,
      event,
      selector,
      timestamp: Date.now(),
    });
  }

  /** Get count of pending events */
  getPendingCount(): number {
    return this.events.length;
  }

  /** Send pending events to server via fetch */
  async flush(): Promise<void> {
    if (this.events.length === 0) return;

    const reports = this.takeReports();

    for (const report of reports) {
      try {
        await fetch(`${this.serverUrl}/api/v1/report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': this.apiKey,
          },
          body: JSON.stringify(report),
        });
      } catch {
        // Network failure — events are lost. This is acceptable for analytics.
      }
    }
  }

  /** Send pending events via sendBeacon (for page unload) */
  private flushBeacon(): void {
    if (document.visibilityState !== 'hidden' && this.running) return;
    if (this.events.length === 0) return;

    for (const report of this.takeReports()) {
      try {
        // text/plain: CORS-safelisted, so the beacon goes out without a
        // preflight; the key rides in the query because sendBeacon cannot set
        // headers — the server accepts both only for POST /report (N4-3).
        const blob = new Blob([JSON.stringify(report)], { type: 'text/plain;charset=UTF-8' });
        navigator.sendBeacon(`${this.serverUrl}/api/v1/report?key=${encodeURIComponent(this.apiKey)}`, blob);
      } catch {
        // sendBeacon not available or failed — acceptable for analytics
      }
    }
  }

  /** Alle offenen Ereignisse als Berichte zu je hoechstens REPORT_CHUNK. */
  private takeReports(): AnalyticsReport[] {
    // Ohne Fragment, Tracking- und Review-Parameter (scanner#358): im
    // Review-Modus stand hier sonst `?t=<jwt>`, auf Kundenseiten
    // `#access_token=…` — beides landete in analytics_reports.url.
    const url = reportPageUrl(window.location.href);
    const reports: AnalyticsReport[] = [];
    for (let i = 0; i < this.events.length; i += PatchAnalytics.REPORT_CHUNK) {
      reports.push({ url, events: this.events.slice(i, i + PatchAnalytics.REPORT_CHUNK), sessionId: this.sessionId });
    }
    this.events = [];
    return reports;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
